import { useIntl } from 'react-intl';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { CheckboxGroup, type CheckboxItem } from '../CheckboxGroup';

type FilterEntry = { id: string; value: string[] };

type Props = {
  onFilter?: (filters: FilterEntry[]) => void;
  value?: string[];
};

export default function Goals({ onFilter, value = [] }: Props) {
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);

  const options: CheckboxItem[] = [
    {
      id: 'impressions',
      label: formatMessage({
        defaultMessage: 'Impressions',
        id: 'campaigns.lineItems.advancedFilters.goal.impressions'
      })
    },
    {
      id: 'clicks',
      label: formatMessage({
        defaultMessage: 'Clicks',
        id: 'campaigns.lineItems.advancedFilters.goal.clicks'
      })
    },
    {
      id: 'videoStart',
      label: formatMessage({
        defaultMessage: 'Video Start',
        id: 'campaigns.lineItems.advancedFilters.goal.videoStart'
      }),
      show: checkUserResourcePermissions('campaignGoalVideo_start', user.toJSON(), userRoles)
    },
    {
      id: 'videoComplete',
      label: formatMessage({
        defaultMessage: 'Video Complete',
        id: 'campaigns.lineItems.advancedFilters.goal.videoComplete'
      }),
      show: checkUserResourcePermissions('campaignGoalVideo_complete', user.toJSON(), userRoles)
    },
    {
      id: 'storeVisits',
      label: formatMessage({
        defaultMessage: 'Store Visits',
        id: 'campaigns.lineItems.advancedFilters.goal.storeVisits'
      })
    },
    {
      id: 'audioStart',
      label: formatMessage({
        defaultMessage: 'Audio Start',
        id: 'campaigns.lineItems.advancedFilters.goal.audioStart'
      }),
      show: checkUserResourcePermissions('campaignGoalAudio_start', user.toJSON(), userRoles)
    },
    {
      id: 'audioComplete',
      label: formatMessage({
        defaultMessage: 'Audio Complete',
        id: 'campaigns.lineItems.advancedFilters.goal.audioComplete'
      }),
      show: checkUserResourcePermissions('campaignGoalAudio_complete', user.toJSON(), userRoles)
    },
    {
      id: 'pagesViewed',
      label: formatMessage({
        defaultMessage: 'Pages Viewed',
        id: 'campaigns.lineItems.advancedFilters.goal.pagesViewed'
      })
    },
    {
      id: 'engagements',
      label: formatMessage({
        defaultMessage: 'Engagements',
        id: 'campaigns.lineItems.advancedFilters.goal.engagements'
      })
    }
  ];

  const handleChange = (_id: string, selected: string[]) => {
    const filter: FilterEntry[] = [{ id: 'goal', value: selected }];
    onFilter?.(filter);
  };

  return (
    <CheckboxGroup
      id="goal"
      onChange={handleChange}
      options={options}
      selected={value}
      title={formatMessage({
        defaultMessage: 'Goal',
        id: 'campaigns.lineItems.advancedFilters.goal.title'
      })}
    />
  );
}
